// Proposal builder page (session-1 MVP).
//
// Functional block editor with add/edit/delete + live-computed totals.
// Session 2 will layer: drag-and-drop reordering, richer per-block editors
// (rich text toolbar reuse, cover image upload, media, file), header options
// panel with gear icon, and access-control UI polish.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Save, Trash2, Plus, Send, Copy, ExternalLink, Eye,
  Type, AlignLeft, DollarSign, ImageIcon, Paperclip, Image as ImageIco, Video as VideoIcon,
  ChevronUp, ChevronDown, Settings2, Check, X, Loader2, Download, GripVertical,
  BarChart3, Clock, Fingerprint, Upload,
} from "lucide-react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api, { API_BASE, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import PragmaSelect from "../components/PragmaSelect";
import { useConfirm } from "../components/ConfirmModal";
import { useAuth } from "../context/AuthContext";
import RichTextEditor from "../components/RichTextEditor";
import ProposalImageUpload from "../components/ProposalImageUpload";
import { AssetPickerModal } from "../components/AssetLibrary";
import { VideoBlockEditor, VideoBlockRender, defaultVideoContent } from "../components/VideoBlock";

// Curated gradient banners for new proposals — matches the vibe of Propoze etc.
const HEADER_GRADIENTS = [
  { id: "iris",      style: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" },
  { id: "seafoam",   style: "linear-gradient(135deg, #43cea2 0%, #185a9d 100%)" },
  { id: "sunset",    style: "linear-gradient(135deg, #ff9966 0%, #ff5e62 100%)" },
  { id: "aurora",    style: "linear-gradient(135deg, #7f7fd5 0%, #86a8e7 50%, #91eae4 100%)" },
  { id: "pragma",    style: "linear-gradient(135deg, #5B4FE8 0%, #8b7cff 100%)" },
  { id: "ember",     style: "linear-gradient(135deg, #f6d365 0%, #fda085 100%)" },
  { id: "midnight",  style: "linear-gradient(135deg, #0f172a 0%, #334155 100%)" },
  { id: "coral",     style: "linear-gradient(135deg, #ff6a88 0%, #ffb199 100%)" },
];
// Deterministic default so a given proposal always renders the same gradient
// until the user picks something else.
function defaultGradient(proposalId) {
  let sum = 0;
  for (let i = 0; i < (proposalId || "").length; i++) sum = (sum + proposalId.charCodeAt(i)) | 0;
  return HEADER_GRADIENTS[Math.abs(sum) % HEADER_GRADIENTS.length];
}

const BLOCK_LIB = [
  { type: "heading",     label: "Heading",     icon: Type,        hint: "Section title" },
  { type: "rich_text",   label: "Rich text",   icon: AlignLeft,   hint: "Paragraphs, lists, links" },
  { type: "service",     label: "Service",     icon: DollarSign,  hint: "A priced line item" },
  { type: "cover_image", label: "Cover image", icon: ImageIco,    hint: "Sits above the fold" },
  { type: "media",       label: "Media",       icon: ImageIcon,   hint: "Inline image" },
  { type: "video",       label: "Video",       icon: VideoIcon,   hint: "YouTube, Vimeo, Loom, Wistia" },
  { type: "file",        label: "File",        icon: Paperclip,   hint: "Attachment" },
];

const UNITS = [
  { value: "Fixed fee", label: "Fixed fee" },
  { value: "Investment", label: "Investment" },
  { value: "Hour", label: "Hour" },
  { value: "Day", label: "Day" },
  { value: "Month", label: "Month" },
  { value: "Year", label: "Year" },
];

const fmtMoney = (currency, n) => {
  const v = Number.isFinite(+n) ? +n : 0;
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD" }).format(v);
  } catch { return `${currency || "USD"} ${v.toFixed(2)}`; }
};

export default function ProposalBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user } = useAuth();

  const [state, setState] = useState({ loading: true, proposal: null, blocks: [], totals: null });
  const [deliverables, setDeliverables] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/proposals/${id}`);
      // Upload-type proposals use the dedicated signing-page builder.
      if (data.proposal?.kind === "upload") {
        navigate(`/proposals/${id}/sign-builder`, { replace: true });
        return;
      }
      setState({ loading: false, proposal: data.proposal, blocks: data.blocks, totals: data.totals });
      // Load analytics only for published+ proposals — analytics live even after status changes.
      if (["published", "accepted", "rejected"].includes(data.proposal.status)) {
        try {
          const { data: a } = await api.get(`/proposals/${id}/analytics`);
          setAnalytics(a);
        } catch { /* silent */ }
      } else {
        setAnalytics(null);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load proposal");
      navigate("/proposals");
    }
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/deliverables");
        setDeliverables(Array.isArray(data) ? data : []);
      } catch { /* silent */ }
    })();
  }, []);

  // Debounced save of the proposal header fields
  const patchProposal = (updates) => {
    setState((s) => ({ ...s, proposal: { ...s.proposal, ...updates } }));
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await api.patch(`/proposals/${id}`, updates);
        await load(); // re-fetch to refresh totals
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
      } finally {
        setSaving(false);
      }
    }, 500);
  };

  const addBlock = async (type, content = {}) => {
    try {
      await api.post(`/proposals/${id}/blocks`, { type, content });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add block");
    }
  };

  const updateBlock = async (bid, content) => {
    // Optimistic local update, save, then re-fetch to refresh totals
    setState((s) => ({
      ...s,
      blocks: s.blocks.map((b) => b.id === bid ? { ...b, content } : b),
    }));
    try {
      await api.patch(`/proposals/${id}/blocks/${bid}`, { content });
      load();
    } catch (e) {
      toast.error("Could not save block");
    }
  };

  const deleteBlock = async (bid) => {
    const ok = await confirm({ title: "Delete this block?", message: "The block will be removed from the proposal.", confirmLabel: "Delete", destructive: true });
    if (!ok) return;
    await api.delete(`/proposals/${id}/blocks/${bid}`);
    load();
  };

  const moveBlock = async (bid, dir) => {
    // Kept for keyboard fallback; primary UX is drag-and-drop via dnd-kit.
    const ids = state.blocks.map((b) => b.id);
    const idx = ids.indexOf(bid);
    if (idx === -1) return;
    const swapWith = dir === "up" ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= ids.length) return;
    [ids[idx], ids[swapWith]] = [ids[swapWith], ids[idx]];
    await api.post(`/proposals/${id}/blocks/reorder`, { block_ids: ids });
    load();
  };

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = state.blocks.findIndex((b) => b.id === active.id);
    const newIdx = state.blocks.findIndex((b) => b.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(state.blocks, oldIdx, newIdx);
    setState((s) => ({ ...s, blocks: reordered })); // optimistic
    try {
      await api.post(`/proposals/${id}/blocks/reorder`, { block_ids: reordered.map((b) => b.id) });
    } catch { toast.error("Reorder failed"); load(); }
  };

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem("pragma_token");
      const res = await fetch(`${API_BASE}/proposals/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${state.proposal.number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("Could not export PDF"); }
  };

  const publish = async () => {
    try {
      await api.post(`/proposals/${id}/publish`);
      toast.success("Proposal published");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Publish failed");
    }
  };

  const unpublish = async () => {
    await api.post(`/proposals/${id}/unpublish`);
    load();
  };

  const copyPublicLink = async () => {
    const p = state.proposal;
    if (!p) return;
    // Prefer the slug from the authed user; if missing (legacy tenant), fall
    // back to the slug-less URL — the backend accepts both.
    const slug = user?.tenant_slug || "";
    const url = slug
      ? `${window.location.origin}/p/${slug}/${p.public_token}`
      : `${window.location.origin}/p/${p.public_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.info(url);
    }
  };

  if (state.loading || !state.proposal) {
    return <div className="p-10 text-center text-sm" style={{ color: "var(--pg-text-mute)" }}>Loading…</div>;
  }

  const p = state.proposal;
  const isEditable = ["draft", "published"].includes(p.status);
  const totals = state.totals || {};
  const currency = p.currency || "USD";

  return (
    <div className="max-w-5xl mx-auto pb-24" data-testid="proposal-builder">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate("/proposals")} className="pg-btn pg-btn-ghost" data-testid="proposal-back">
          <ArrowLeft size={14} /> Back to Proposals
        </button>
        <div className="flex items-center gap-2">
          {saving && (
            <span className="text-xs flex items-center gap-1" style={{ color: "var(--pg-text-mute)" }}>
              <Loader2 size={11} className="animate-spin" /> Saving…
            </span>
          )}
          <button onClick={() => setShowOptions(true)} className="pg-btn pg-btn-ghost" data-testid="proposal-options-btn">
            <Settings2 size={14} /> Options
          </button>
          <button onClick={downloadPdf} className="pg-btn pg-btn-ghost" data-testid="proposal-pdf-btn">
            <Download size={14} /> Download PDF
          </button>
          {p.status === "draft" && (
            <button onClick={publish} className="pg-btn pg-btn-primary" data-testid="proposal-publish-btn">
              <Send size={14} /> Publish
            </button>
          )}
          {p.status === "published" && (
            <>
              <button onClick={copyPublicLink} className="pg-btn pg-btn-ghost" data-testid="proposal-copy-link">
                <Copy size={14} /> Copy client link
              </button>
              <button onClick={unpublish} className="pg-btn pg-btn-ghost" data-testid="proposal-unpublish-btn">
                Unpublish
              </button>
            </>
          )}
          {p.status === "accepted" && (
            <span
              style={{
                padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 500,
                color: "#16a34a", background: "rgba(22,163,74,0.12)",
              }}
              data-testid="proposal-accepted-badge"
            >
              Accepted
            </span>
          )}
        </div>
      </div>

      {/* Analytics — visible once the proposal is out in the world */}
      {analytics && <AnalyticsPanel analytics={analytics} />}

      {/* Header banner */}
      <ProposalHeaderBanner
        proposal={p}
        editable={isEditable}
        onChange={(url) => patchProposal({ header_image_url: url })}
      />

      {/* Header fields */}
      <div className="pg-card p-6 mb-6" data-testid="proposal-header">
        <div className="mono text-[10px] uppercase tracking-wider mb-2" style={{ color: "var(--pg-accent)" }}>
          {p.number} · for {p.deal_name}
        </div>
        <input
          value={p.name || ""}
          onChange={(e) => patchProposal({ name: e.target.value })}
          disabled={!isEditable}
          className="w-full mb-4"
          style={{
            fontSize: 26, fontWeight: 600, background: "transparent",
            border: "none", outline: "none", color: "var(--pg-text)",
          }}
          data-testid="proposal-name"
          placeholder="Proposal name"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Currency">
            <PragmaSelect
              value={p.currency}
              onChange={(v) => patchProposal({ currency: v })}
              options={["USD","EUR","GBP","AUD","CAD","NZD","JPY","INR","SGD","AED"].map(c => ({ value: c, label: c }))}
              testId="proposal-currency"
              disabled={!isEditable}
              size="sm"
            />
          </Field>
          <Field label="Display date">
            <input
              type="date"
              value={(p.display_date || "").slice(0, 10)}
              onChange={(e) => patchProposal({ display_date: e.target.value })}
              disabled={!isEditable}
              className="pg-input"
              data-testid="proposal-display-date"
              style={{ width: "100%" }}
            />
          </Field>
          <Field label="Valid until">
            <input
              type="date"
              value={(p.valid_until || "").slice(0, 10)}
              onChange={(e) => patchProposal({ valid_until: e.target.value })}
              disabled={!isEditable}
              className="pg-input"
              data-testid="proposal-valid-until"
              style={{ width: "100%" }}
            />
          </Field>
        </div>
      </div>

      {/* Blocks */}
      <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={state.blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {state.blocks.map((b) => (
              <SortableBlock
                key={b.id}
                block={b}
                currency={currency}
                deliverables={deliverables}
                editable={isEditable}
                onChange={(content) => updateBlock(b.id, content)}
                onDelete={() => deleteBlock(b.id)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Add block palette */}
      {isEditable && (
        <div className="mt-4 pg-card p-4" data-testid="proposal-add-palette">
          <div className="text-xs mb-3" style={{ color: "var(--pg-text-mute)" }}>Add a block</div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            {BLOCK_LIB.map((b) => (
              <button
                key={b.type}
                onClick={() => addBlock(b.type, defaultContent(b.type))}
                data-testid={`proposal-add-${b.type}`}
                className="p-3 text-left"
                style={{
                  border: "1px solid var(--pg-border)",
                  borderRadius: 8,
                  background: "transparent",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--pg-accent)"}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = "var(--pg-border)"}
              >
                <b.icon size={16} style={{ color: "var(--pg-accent)", marginBottom: 6 }} />
                <div style={{ fontSize: 13, fontWeight: 500 }}>{b.label}</div>
                <div className="text-xs" style={{ color: "var(--pg-text-mute)" }}>{b.hint}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Totals */}
      <div className="pg-card p-6 mt-6" data-testid="proposal-totals">
        <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, marginBottom: 12 }}>Summary</h3>
        {(totals.by_unit || []).length > 0 ? (
          <table className="w-full text-sm">
            <tbody>
              {totals.by_unit.map((row) => (
                <tr key={row.unit}>
                  <td className="py-1" style={{ color: "var(--pg-text-mute)" }}>{row.unit} services</td>
                  <td className="py-1 text-right">{fmtMoney(currency, row.amount)}</td>
                </tr>
              ))}
              {totals.subtotal_discount_amount > 0 && (
                <tr>
                  <td className="py-1" style={{ color: "var(--pg-text-mute)" }}>Subtotal discount</td>
                  <td className="py-1 text-right" style={{ color: "#dc2626" }}>−{fmtMoney(currency, totals.subtotal_discount_amount)}</td>
                </tr>
              )}
              <tr style={{ borderTop: "1px solid var(--pg-border)" }}>
                <td className="py-2" style={{ color: "var(--pg-text-mute)" }}>Subtotal</td>
                <td className="py-2 text-right">{fmtMoney(currency, totals.subtotal)}</td>
              </tr>
              <tr>
                <td className="py-1" style={{ color: "var(--pg-text-mute)" }}>Tax ({totals.vat_percent}%)</td>
                <td className="py-1 text-right">{fmtMoney(currency, totals.vat_amount)}</td>
              </tr>
              <tr style={{ borderTop: "1px solid var(--pg-border)" }}>
                <td className="py-2" style={{ fontWeight: 600 }}>Total</td>
                <td className="py-2 text-right" style={{ fontWeight: 600, fontSize: 16 }}>{fmtMoney(currency, totals.grand_total)}</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p className="text-sm" style={{ color: "var(--pg-text-mute)" }}>
            Add a Service block to start pricing this proposal.
          </p>
        )}

        {/* Subtotal discount + VAT controls */}
        {isEditable && (
          <div className="mt-5 pt-4" style={{ borderTop: "1px solid var(--pg-border)" }}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Subtotal discount">
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={p.subtotal_discount || 0}
                    onChange={(e) => patchProposal({ subtotal_discount: Number(e.target.value) || 0 })}
                    className="pg-input"
                    style={{ flex: 1 }}
                    data-testid="proposal-subtotal-discount"
                  />
                  <button
                    onClick={() => patchProposal({ subtotal_discount_kind: p.subtotal_discount_kind === "percent" ? "amount" : "percent" })}
                    className="pg-btn pg-btn-ghost"
                    style={{ minWidth: 36 }}
                    data-testid="proposal-discount-kind-toggle"
                  >
                    {p.subtotal_discount_kind === "percent" ? "%" : "$"}
                  </button>
                </div>
              </Field>
              <Field label="Tax %">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={p.vat_percent || 0}
                  onChange={(e) => patchProposal({ vat_percent: Number(e.target.value) || 0 })}
                  className="pg-input"
                  style={{ width: "100%" }}
                  data-testid="proposal-vat-percent"
                />
              </Field>
              <Field label="Additional information">
                <textarea
                  value={p.additional_information || ""}
                  onChange={(e) => patchProposal({ additional_information: e.target.value })}
                  className="pg-input"
                  style={{ width: "100%", minHeight: 60 }}
                  placeholder="Payment terms footnote, etc."
                  data-testid="proposal-additional-info"
                />
              </Field>
            </div>
          </div>
        )}
      </div>

      {showOptions && (
        <OptionsModal
          proposal={p}
          onClose={() => setShowOptions(false)}
          onPatch={patchProposal}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sortable wrapper for a block — wires dnd-kit's transform onto the card
// and gives the user a visible grip handle on the left edge.
// ---------------------------------------------------------------------------
function SortableBlock({ block, currency, deliverables, editable, onChange, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id, disabled: !editable });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <BlockEditor
        block={block}
        currency={currency}
        deliverables={deliverables}
        editable={editable}
        onChange={onChange}
        onDelete={onDelete}
        dragHandle={editable ? (
          <button
            className="pg-btn pg-btn-ghost cursor-grab active:cursor-grabbing"
            style={{ padding: 4, color: "var(--pg-text-mute)" }}
            {...attributes}
            {...listeners}
            title="Drag to reorder"
            data-testid={`block-drag-${block.id}`}
          >
            <GripVertical size={14} />
          </button>
        ) : null}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Block editors — one component with a switch on type
// ---------------------------------------------------------------------------
function BlockEditor({ block, currency, deliverables, editable, onChange, onDelete, dragHandle }) {
  const c = block.content || {};
  const set = (patch) => onChange({ ...c, ...patch });

  return (
    <div className="pg-card p-4 relative" data-testid={`proposal-block-${block.id}`}>
      {editable && (
        <div className="absolute top-2 right-2 flex items-center gap-1 opacity-60 hover:opacity-100 transition-opacity">
          <button onClick={onDelete} className="pg-btn pg-btn-ghost" style={{ padding: 4, color: "#dc2626" }} title="Delete">
            <Trash2 size={13} />
          </button>
        </div>
      )}
      {dragHandle && (
        <div className="absolute top-2 left-2 opacity-50 hover:opacity-100 transition-opacity">
          {dragHandle}
        </div>
      )}
      <div style={{ paddingLeft: dragHandle ? 22 : 0, paddingRight: 22 }}>
      {block.type === "heading" && (
        <>
          <input
            value={c.heading || ""}
            onChange={(e) => set({ heading: e.target.value })}
            disabled={!editable}
            placeholder="Heading (e.g. Scope)"
            className="w-full mb-2"
            style={{ fontSize: 20, fontWeight: 600, background: "transparent", border: "none", outline: "none", color: "var(--pg-text)" }}
            data-testid={`block-heading-${block.id}`}
          />
          <input
            value={c.subheading || ""}
            onChange={(e) => set({ subheading: e.target.value })}
            disabled={!editable}
            placeholder="Optional subheading"
            className="w-full"
            style={{ fontSize: 13, background: "transparent", border: "none", outline: "none", color: "var(--pg-text-mute)" }}
            data-testid={`block-subheading-${block.id}`}
          />
        </>
      )}

      {block.type === "rich_text" && (
        <RichTextEditor
          value={c.html || ""}
          onChange={(html) => set({ html })}
          disabled={!editable}
          placeholder="Type paragraphs here…"
          testId={`block-rich-${block.id}`}
        />
      )}

      {block.type === "service" && (
        <ServiceBlock c={c} set={set} currency={currency} deliverables={deliverables} editable={editable} bid={block.id} />
      )}

      {(block.type === "cover_image" || block.type === "media") && (
        <ProposalImageUpload
          value={{ url: c.url || "" }}
          onChange={({ url }) => set({ url })}
          kind="image"
          disabled={!editable}
          testId={`block-image-${block.id}`}
          height={block.type === "cover_image" ? 200 : 140}
        />
      )}

      {block.type === "file" && (
        <div>
          <ProposalImageUpload
            value={{ url: c.url || "", filename: c.label }}
            onChange={({ url, filename }) => set({ url, label: c.label || filename })}
            kind="file"
            disabled={!editable}
            testId={`block-file-${block.id}`}
            height={100}
          />
          {c.url && (
            <input
              value={c.label || ""}
              onChange={(e) => set({ label: e.target.value })}
              disabled={!editable}
              placeholder="Label shown to client"
              className="pg-input w-full mt-2"
              data-testid={`block-file-label-${block.id}`}
            />
          )}
        </div>
      )}

      {block.type === "video" && (
        editable ? (
          <VideoBlockEditor value={c} onChange={set} testIdPrefix={`block-video-${block.id}`} />
        ) : (
          <VideoBlockRender video={c} compact />
        )
      )}
      </div>
    </div>
  );
}

function ServiceBlock({ c, set, currency, deliverables, editable, bid }) {
  const qty = Number(c.quantity || 0);
  const price = Number(c.unit_price || 0);
  const disc = Number(c.discount_pct || 0);
  const gross = qty * price;
  const net = gross * (1 - disc / 100);

  const dOpts = [
    { value: "", label: "— Pick from Deliverables — (optional)" },
    ...deliverables.filter(d => d.active !== false).map(d => ({
      value: d.id, label: d.name, hint: d.category,
    })),
  ];

  const pickDeliverable = (did) => {
    if (!did) { set({ deliverable_id: null }); return; }
    const d = deliverables.find(x => x.id === did);
    if (!d) return;
    set({
      deliverable_id: d.id,
      name_snapshot: d.name,
      description_snapshot: d.description || "",
      unit_price: d.default_price || 0,
    });
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <Field label="Service (from catalogue)">
          <PragmaSelect
            value={c.deliverable_id || ""}
            onChange={pickDeliverable}
            options={dOpts}
            disabled={!editable}
            size="sm"
            testId={`block-service-picker-${bid}`}
          />
        </Field>
        <Field label="Or type a name">
          <input
            value={c.name_snapshot || ""}
            onChange={(e) => set({ name_snapshot: e.target.value })}
            disabled={!editable}
            className="pg-input w-full"
            placeholder="e.g. Discovery workshop"
            data-testid={`block-service-name-${bid}`}
          />
        </Field>
      </div>

      <Field label="Description">
        <textarea
          value={c.description_snapshot || ""}
          onChange={(e) => set({ description_snapshot: e.target.value })}
          disabled={!editable}
          className="pg-input w-full"
          style={{ minHeight: 60 }}
          placeholder="What's included in this service…"
          data-testid={`block-service-desc-${bid}`}
        />
      </Field>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
        <Field label="Unit">
          <PragmaSelect
            value={c.unit || "Fixed fee"}
            onChange={(v) => set({ unit: v })}
            options={UNITS}
            disabled={!editable}
            size="sm"
            testId={`block-service-unit-${bid}`}
          />
        </Field>
        <Field label="Quantity">
          <input
            type="number" min="0" step="1"
            value={c.quantity ?? ""}
            onChange={(e) => set({ quantity: Number(e.target.value) || 0 })}
            disabled={!editable}
            className="pg-input w-full"
            data-testid={`block-service-qty-${bid}`}
          />
        </Field>
        <Field label="Unit price">
          <input
            type="number" min="0" step="0.01"
            value={c.unit_price ?? ""}
            onChange={(e) => set({ unit_price: Number(e.target.value) || 0 })}
            disabled={!editable}
            className="pg-input w-full"
            data-testid={`block-service-price-${bid}`}
          />
        </Field>
        <Field label="Discount %">
          <input
            type="number" min="0" max="100" step="0.5"
            value={c.discount_pct ?? ""}
            onChange={(e) => set({ discount_pct: Number(e.target.value) || 0 })}
            disabled={!editable}
            className="pg-input w-full"
            data-testid={`block-service-disc-${bid}`}
          />
        </Field>
      </div>

      <div className="mt-3 text-right text-sm" style={{ color: "var(--pg-text-mute)" }}>
        Line total: <strong style={{ color: "var(--pg-text)" }}>{fmtMoney(currency, net)}</strong>
      </div>
    </div>
  );
}

function defaultContent(type) {
  if (type === "heading")   return { heading: "", subheading: "" };
  if (type === "rich_text") return { html: "" };
  if (type === "service")   return { unit: "Fixed fee", quantity: 1, unit_price: 0, discount_pct: 0 };
  if (type === "cover_image" || type === "media") return { url: "" };
  if (type === "file")      return { url: "", label: "" };
  if (type === "video")     return defaultVideoContent();
  return {};
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block mb-1 text-xs" style={{ color: "var(--pg-text-mute)" }}>{label}</label>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Options modal — hide prices, allow response, restrict access
// ---------------------------------------------------------------------------
function OptionsModal({ proposal, onClose, onPatch }) {
  const [localEmails, setLocalEmails] = useState((proposal.allowed_emails || []).join("\n"));
  const save = () => {
    const emails = localEmails.split(/[\n,]/).map(e => e.trim()).filter(Boolean);
    onPatch({ allowed_emails: emails });
    onClose();
  };
  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 200, background: "rgba(15,23,42,0.55)" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="proposal-options-modal"
    >
      <div style={{
        width: 520, maxWidth: "94vw", background: "var(--pg-surface)",
        borderRadius: 14, border: "1px solid var(--pg-border)", padding: 24,
        boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
      }}>
        <div className="flex items-center justify-between mb-4">
          <h3 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>Proposal options</h3>
          <button onClick={onClose} className="pg-btn pg-btn-ghost" style={{ padding: 6 }}><X size={14} /></button>
        </div>
        <Toggle
          label="Hide prices per line"
          hint="Only show the grand total to the client."
          value={proposal.hide_prices}
          onChange={(v) => onPatch({ hide_prices: v })}
          testid="option-hide-prices"
        />
        <Toggle
          label="Allow client to accept or reject"
          hint="Turn off if you're using this as a reference document only."
          value={proposal.allow_response}
          onChange={(v) => onPatch({ allow_response: v })}
          testid="option-allow-response"
        />
        <Toggle
          label="Restrict access by email"
          hint="Only the emails below can open the proposal link."
          value={proposal.restrict_access}
          onChange={(v) => onPatch({ restrict_access: v })}
          testid="option-restrict-access"
        />
        {proposal.restrict_access && (
          <div className="mt-3">
            <label className="block mb-1 text-xs" style={{ color: "var(--pg-text-mute)" }}>Allowed emails (one per line)</label>
            <textarea
              value={localEmails}
              onChange={(e) => setLocalEmails(e.target.value)}
              className="pg-input w-full"
              style={{ minHeight: 90 }}
              placeholder="jane@client.com&#10;john@client.com"
              data-testid="option-allowed-emails"
            />
          </div>
        )}
        <div className="flex items-center justify-end gap-2 mt-6">
          <button onClick={onClose} className="pg-btn pg-btn-ghost">Cancel</button>
          <button onClick={save} className="pg-btn pg-btn-primary" data-testid="option-save">Save</button>
        </div>
      </div>
    </div>
  );
}

function Toggle({ label, hint, value, onChange, testid }) {
  return (
    <label className="flex items-start justify-between gap-4 py-3" style={{ borderBottom: "1px solid var(--pg-border)", cursor: "pointer" }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 500 }}>{label}</div>
        {hint && <div className="text-xs mt-0.5" style={{ color: "var(--pg-text-mute)" }}>{hint}</div>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        data-testid={testid}
        style={{
          width: 40, height: 22, borderRadius: 100,
          background: value ? "var(--pg-accent)" : "var(--pg-border)",
          border: "none", cursor: "pointer", position: "relative",
          transition: "background 140ms",
          flexShrink: 0, marginTop: 2,
        }}
      >
        <span style={{
          position: "absolute", top: 3, left: value ? 21 : 3,
          width: 16, height: 16, borderRadius: "50%",
          background: "#fff", transition: "left 140ms",
        }} />
      </button>
    </label>
  );
}


// ---------------------------------------------------------------------------
// Analytics panel — opens count, last viewed, signature summary
// ---------------------------------------------------------------------------
function AnalyticsPanel({ analytics }) {
  const { view_count = 0, last_viewed_at, view_log = [], signature_name, signature_date, decision_email } = analytics || {};
  const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
  return (
    <div className="pg-card p-5 mb-6" data-testid="proposal-analytics">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 size={14} style={{ color: "var(--pg-accent)" }} />
        <h3 className="mono text-[10px] uppercase tracking-wider" style={{ color: "var(--pg-text-mute)", margin: 0 }}>Activity</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Metric label="Client opens" value={view_count} testid="analytics-views" />
        <Metric label="Last opened" value={fmt(last_viewed_at)} small testid="analytics-last-viewed" />
        {signature_name ? (
          <>
            <Metric label="Accepted by" value={signature_name} small testid="analytics-signature-name" />
            <Metric label="Signed on" value={fmt(signature_date)} small testid="analytics-signature-date" />
          </>
        ) : (
          <>
            <Metric label="Recipient" value={decision_email || "—"} small />
            <Metric label="Signed on" value="Awaiting acceptance" small />
          </>
        )}
      </div>
      {view_log.length > 0 && (
        <details className="mt-4" style={{ fontSize: 12 }}>
          <summary style={{ color: "var(--pg-text-mute)", cursor: "pointer", fontSize: 12 }}>
            Show full open history ({view_log.length})
          </summary>
          <ul style={{ margin: "10px 0 0", paddingLeft: 16, color: "var(--pg-text-mute)" }}>
            {view_log.slice().reverse().slice(0, 25).map((e, i) => (
              <li key={i} className="mb-1">
                <Clock size={10} style={{ display: "inline", marginRight: 4 }} />
                {fmt(e.ts)}{e.email ? ` · ${e.email}` : ""}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Metric({ label, value, small, testid }) {
  return (
    <div data-testid={testid}>
      <div className="mono text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--pg-text-mute)" }}>{label}</div>
      <div style={{ fontSize: small ? 13 : 22, fontWeight: small ? 500 : 600 }}>{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header banner — full-width visual above the proposal title, à la Propoze
// ---------------------------------------------------------------------------
function ProposalHeaderBanner({ proposal, editable, onChange }) {
  const [picking, setPicking] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const fileRef = useRef(null);

  const chosenUrl = proposal.header_image_url || "";
  const grad = defaultGradient(proposal.id);
  const showImage = chosenUrl && !chosenUrl.startsWith("gradient:");
  const gradientId = chosenUrl.startsWith("gradient:") ? chosenUrl.slice("gradient:".length) : null;
  const gradient = gradientId ? (HEADER_GRADIENTS.find(g => g.id === gradientId)?.style) : grad.style;

  const upload = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/proposals/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(`${window.location.origin}${data.url}`);
      setPicking(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <>
    <div
      data-testid="proposal-header-banner"
      style={{
        position: "relative", height: 160, borderRadius: 12, marginBottom: 16,
        overflow: "hidden",
        background: showImage ? undefined : gradient,
      }}
    >
      {showImage && (
        <img src={chosenUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      )}
      {editable && (
        <button
          onClick={() => setPicking(!picking)}
          data-testid="proposal-header-change"
          style={{
            position: "absolute", top: 12, right: 12,
            padding: "6px 12px", fontSize: 12, fontWeight: 500,
            background: "rgba(15,23,42,0.65)", color: "#fff",
            border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6,
            cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
            backdropFilter: "blur(8px)",
          }}
        >
          <ImageIcon size={12} /> Change banner
        </button>
      )}
      {picking && editable && (
        <div
          style={{
            position: "absolute", top: 46, right: 12, zIndex: 20,
            background: "var(--pg-surface)", border: "1px solid var(--pg-border)",
            borderRadius: 10, padding: 14, width: 280,
            boxShadow: "0 12px 32px rgba(0,0,0,0.24)",
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="mono text-[10px] uppercase tracking-wider mb-2" style={{ color: "var(--pg-text-mute)" }}>Pick a gradient</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6, marginBottom: 12 }}>
            {HEADER_GRADIENTS.map((g) => (
              <button
                key={g.id}
                onClick={() => { onChange(`gradient:${g.id}`); setPicking(false); }}
                data-testid={`header-gradient-${g.id}`}
                title={g.id}
                style={{
                  height: 36, borderRadius: 6, border: gradientId === g.id ? "2px solid var(--pg-accent)" : "1px solid var(--pg-border)",
                  background: g.style, cursor: "pointer",
                }}
              />
            ))}
          </div>
          <div className="mono text-[10px] uppercase tracking-wider mb-2" style={{ color: "var(--pg-text-mute)" }}>Or upload your own</div>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="pg-btn pg-btn-ghost w-full"
            style={{ justifyContent: "center", fontSize: 12 }}
            data-testid="header-upload-btn"
          >
            {uploading ? <><Loader2 size={12} className="animate-spin" /> Uploading…</> : <><Upload size={12} /> Choose image</>}
          </button>
          <button
            onClick={() => { setLibraryOpen(true); setPicking(false); }}
            className="pg-btn pg-btn-ghost w-full mt-2"
            style={{ justifyContent: "center", fontSize: 12 }}
            data-testid="header-library-btn"
          >
            <ImageIcon size={12} /> Choose from library
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => upload(e.target.files?.[0])}
          />
          {chosenUrl && (
            <button
              onClick={() => { onChange(""); setPicking(false); }}
              className="pg-btn pg-btn-ghost w-full mt-2"
              style={{ justifyContent: "center", fontSize: 11, color: "var(--pg-text-mute)" }}
              data-testid="header-reset-btn"
            >
              Reset to default
            </button>
          )}
        </div>
      )}
    </div>
    <AssetPickerModal
      open={libraryOpen}
      onClose={() => setLibraryOpen(false)}
      accept="image"
      title="Pick a header image"
      onPick={(a) => onChange(`${window.location.origin}${a.url}`)}
    />
    </>
  );
}

